import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (items) => items[Math.floor(Math.random() * items.length)];

const VARIANTS = [
  [1, 3, 2, 1, 2, 5], [2, 4, 3, -1, 3, 7], [1, -2, 2, 3, 3, 4],
  [3, 1, 4, -2, 2, 3], [2, -3, 5, 1, 1, 4], [4, 2, 3, -2, 2, 5],
  [1, 5, 3, 2, 4, 7], [3, -4, 2, 1, 5, 6], [2, 1, 4, 3, 3, 8],
  [5, -1, 2, -3, 2, 7], [1, -4, 3, 1, 2, 9], [4, 3, 5, -2, 3, 5],
];

function shuffle(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function fraction(numerator, denominator) {
  const sign = denominator < 0 ? -1 : 1;
  const n = numerator * sign;
  const d = Math.abs(denominator);
  const divisor = gcd(n, d);
  return { n: n / divisor, d: d / divisor };
}

function fractionLatex(value) {
  return value.d === 1 ? String(value.n) : `\\frac{${value.n}}{${value.d}}`;
}

function linearLatex(a, b) {
  const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return ax;
  return `${ax}${b > 0 ? `+${b}` : b}`;
}

function buildTask(item, source, fixedOptions = null) {
  const [a, b, c, d, p, q] = item;
  const answer = fraction(q * b - p * d, p * c - q * a);
  const forbidden = fraction(-d, c);
  const answerLatex = fractionLatex(answer);
  const wrongValues = [
    fraction(q * b + p * d, p * c - q * a),
    fraction(q * b + p * d, p * c + q * a),
    fraction(p * d - q * b, p * c - q * a),
    fraction(answer.n + answer.d, answer.d),
  ];
  let options;
  if (fixedOptions) {
    options = fixedOptions.map(([label, latex]) => ({ label, latex, isCorrect: latex === answerLatex }));
  } else {
    const unique = [answerLatex];
    wrongValues.map(fractionLatex).forEach((latex) => {
      if (!unique.includes(latex) && unique.length < 4) unique.push(latex);
    });
    options = shuffle(unique).map((latex, index) => ({
      label: LETTERS[index], latex, isCorrect: latex === answerLatex,
    }));
  }
  return {
    type: "MaturaLinearRationalEquationClosedTask",
    source,
    tags: ["matura", "rownania wymierne"],
    data: {
      a, b, c, d, p, q, answer, answerLatex, forbidden,
      equationLatex: `\\frac{${linearLatex(a, b)}}{${linearLatex(c, d)}}=\\frac{${p}}{${q}}`,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
      maxPoints: 1,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2026_MAJ") {
    return buildTask(
      [1, 2, 3, -1, 2, 5],
      { variant: "original", exam: "MATURA", session: "2026_MAJ", taskNumber: "9" },
      [["A", "\\frac{1}{3}"], ["B", "\\frac{8}{11}"], ["C", "3"], ["D", "12"]]
    );
  }
  let task;
  do {
    task = buildTask(pick(VARIANTS), { variant: "random", basedOn: "MATURA_2026_MAJ_9", taskNumber: "9" });
  } while (task.data.options.length !== 4 || task.data.answer.n * task.data.forbidden.d === task.data.forbidden.n * task.data.answer.d);
  return task;
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Rozwiązaniem równania</p>
      <BlockMath math={data.equationLatex} />
      <p>jest liczba:</p>
      <div style={{ display: "grid", gap: 6 }}>
        {data.options.map((option) => <div key={option.label}><InlineMath math={`${option.label}.\\;${option.latex}`} /></div>)}
      </div>
    </div>
  );
}

export function renderInput({ data }, answer, onChange, disabled) {
  return <ABCDInput options={data.options.map((o, i) => ({ value: i, label: o.label, textNode: o.label }))} value={Number.isInteger(answer?.choiceIndex) ? answer.choiceIndex : null} disabled={disabled} onChange={(choiceIndex) => onChange?.({ choiceIndex, label: data.options[choiceIndex].label, latex: data.options[choiceIndex].latex })} />;
}

export function checkAnswer({ data }, answer) {
  return Number.isInteger(answer?.choiceIndex) && answer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const answer = data.options[data.correctIndex];
  return <InlineMath math={`${answer.label}.\\;x=${answer.latex}`} />;
}

export function renderUserAnswer(answer) {
  return Number.isInteger(answer?.choiceIndex) ? <InlineMath math={`${answer.label}.\\;${answer.latex}`} /> : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Krok 1.</strong> Zapisujemy założenie wynikające z mianownika.</p>
      <BlockMath math={`${linearLatex(data.c, data.d)}\\ne0\\quad\\Rightarrow\\quad x\\ne${fractionLatex(data.forbidden)}`} />
      <p><strong>Krok 2.</strong> Mnożymy równanie na krzyż.</p>
      <BlockMath math={`${data.q}(${linearLatex(data.a, data.b)})=${data.p}(${linearLatex(data.c, data.d)})`} />
      <BlockMath math={`${data.q * data.a}x${data.q * data.b >= 0 ? "+" : ""}${data.q * data.b}=${data.p * data.c}x${data.p * data.d >= 0 ? "+" : ""}${data.p * data.d}`} />
      <p><strong>Krok 3.</strong> Rozwiązujemy równanie liniowe i sprawdzamy założenie.</p>
      <BlockMath math={`x=${data.answerLatex},\\qquad ${data.answerLatex}\\ne${fractionLatex(data.forbidden)}`} />
      <p><strong>Wniosek.</strong> Otrzymana liczba spełnia założenie, więc jest rozwiązaniem równania.</p>
    </div>
  );
}
